import * as THREE from 'three/webgpu';
import { PointsMaterialFactory } from '../../../../material/PointsMaterialFactory';
import type { SketchGroup } from '../../../../mesh/sketch/SketchGroup';
import { SketchPointKind } from '../../../../mesh/sketch/ISketchObject';
import { SketchPointObject } from '../../../../mesh/sketch/SketchPointObject';
import { SketchStyleSplineObject } from '../../../../mesh/sketch/SketchStyleSplineObject';
import type { ProjectionResult, WorldCoordinate } from '../../model/ProjectionResult';
import type {
	StyleSplineReferenceBuilderInput,
} from '../../../../../types/reference/sketch-entity-reference/StyleSplineReferenceBuilder';
import { SketchReferenceInjectedEndpointParentScope } from '../SketchReferenceInjectedEndpointParentScope';

export type {
	StyleSplineReferenceBuilderInput,
} from '../../../../../types/reference/sketch-entity-reference/StyleSplineReferenceBuilder';

/**
 * 样式样条引用构建器。
 * 说明：
 * 1. 样式样条保留独立目标类型，避免与普通样条 PropertyManager 混淆；
 * 2. 辅助控制折线由 `SketchStyleSplineObject` 内部同步，builder 不额外创建；
 * 3. 采样点直接来自投影结果，确保创建链路同步、可回滚。
 */
export class StyleSplineReferenceBuilder {
	/**
	 * 构建样式样条对象。
	 *
	 * @param input 构建输入。
	 * @returns 已完成几何初始化的样式样条对象。
	 */
	public build(input: StyleSplineReferenceBuilderInput): SketchStyleSplineObject {
		const lastControlPointIndex = input.geometryPayload.controlPointWorlds.length - 1;
		const controlPoints = input.geometryPayload.controlPointWorlds
			.map((position, index) => {
				if (index === 0 && input.startPoint) return input.startPoint;
				if (index === lastControlPointIndex && input.endPoint) return input.endPoint;
				return createControlPoint(position, input.targetSketch);
			});
		const startPoint = controlPoints[0];
		const endPoint = controlPoints[controlPoints.length - 1];
		if (!startPoint || !endPoint || controlPoints.length < 2) {
			throw new Error('样式样条引用构建失败：控制点数量不足。');
		}

		const injectedEndpointParentScope = new SketchReferenceInjectedEndpointParentScope([
			input.startPoint,
			input.endPoint,
		]);
		try {
			return new SketchStyleSplineObject({
				referencePlane: input.targetSketch.referencePlane,
				startPoint,
				endPoint,
				controlPoints: controlPoints.slice(1, -1),
				curvePoints: input.geometryPayload.curvePointWorlds.map(toVector3),
				curveMetadata: input.geometryPayload.curveMetadata,
			});
		} catch (error) {
			injectedEndpointParentScope.restore();
			throw error;
		}
	}
}

/**
 * 创建引用链路使用的控制点。
 *
 * @param position 世界坐标。
 * @param targetSketch 目标草图。
 * @returns 控制点对象。
 */
function createControlPoint(
	position: WorldCoordinate,
	targetSketch: SketchGroup,
): SketchPointObject {
	return new SketchPointObject({
		position: toVector3(position),
		pointKind: SketchPointKind.ControlPoint,
		material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
		referencePlane: targetSketch.referencePlane,
	});
}

/**
 * 将世界坐标元组转为向量。
 *
 * @param position 世界坐标元组。
 * @returns 三维向量。
 */
function toVector3(position: WorldCoordinate): THREE.Vector3 {
	return new THREE.Vector3(...position);
}
